import { Suspense, useEffect } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

export default function CookiePolicyPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://www.alwaz.travel';
    const scripts: HTMLScriptElement[] = [];

    document.title = 'Cookie Policy | Alwaz Travels – GDPR Cookie Notice';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'Read the Alwaz Travels Cookie Policy. Learn how we use essential, analytics, and marketing cookies on our website in compliance with UK GDPR and the Privacy and Electronic Communications Regulations.');

    const metaRobots = document.querySelector('meta[name="robots"]');
    if (metaRobots) metaRobots.setAttribute('content', 'noindex, follow');

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Cookie Policy | Alwaz Travels',
      description: 'Alwaz Travels Cookie Policy – how we use essential, analytics, and marketing cookies in compliance with UK GDPR.',
      url: `${siteUrl}/cookie-policy`,
      isPartOf: { '@type': 'WebSite', name: 'Alwaz Travels', url: siteUrl },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: 'Cookie Policy', item: `${siteUrl}/cookie-policy` },
        ],
      },
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
    scripts.push(script);

    return () => {
      scripts.forEach((s) => s.parentNode?.removeChild(s));
      if (metaRobots) metaRobots.setAttribute('content', 'index, follow');
    };
  }, []);

  return (
    <Suspense fallback={<div className="min-h-screen bg-white"></div>}>
      <div className="min-h-screen bg-white">
        <Navbar />
        
        <div className="pt-24 pb-16 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3 mb-4">
              <i className="ri-cookie-line text-4xl text-amber-500"></i>
              <h1 className="text-4xl md:text-5xl font-bold text-white">Cookie Policy</h1>
            </div>
            <p className="text-gray-300 text-lg">
              Last Updated: January 2025
            </p>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="prose prose-lg max-w-none">
            
            {/* Introduction */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <i className="ri-information-line text-amber-600"></i>
                Introduction
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                This Cookie Policy explains how <strong>Alwaz Travel</strong> (operated by <strong>Nex Pro Travels Limited</strong>, 
                registered in England and Wales, Company Number 15976699) uses cookies and similar tracking technologies when you visit our website 
                <strong> www.alwaz.travel</strong>.
              </p>
              <p className="text-gray-700 leading-relaxed">
                By using our website, you consent to the use of cookies in accordance with this policy. 
                If you do not agree to our use of cookies, you should adjust your browser settings accordingly or refrain from using our website.
              </p>
            </section>

            {/* What Are Cookies */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <i className="ri-question-line text-amber-600"></i>
                What Are Cookies?
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Cookies are small text files that are placed on your device (computer, smartphone, or tablet) when you visit a website. 
                They are widely used to make websites work more efficiently and provide information to website owners.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Cookies can be "persistent" (remain on your device until deleted or expired) or "session" (deleted when you close your browser).
              </p>
            </section>

            {/* Types of Cookies We Use */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <i className="ri-list-check text-amber-600"></i>
                Types of Cookies We Use
              </h2>

              {/* Essential Cookies */}
              <div className="bg-green-50 border-l-4 border-green-600 p-6 mb-6 rounded-r-lg">
                <h3 className="text-xl font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <i className="ri-shield-check-line text-green-600"></i>
                  1. Essential Cookies (Strictly Necessary)
                </h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  These cookies are essential for the website to function properly. They enable core functionality such as security, 
                  network management, and accessibility. You cannot opt out of these cookies.
                </p>
                <div className="bg-white rounded-lg p-4 mt-4">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-2 px-3 font-semibold text-gray-900">Cookie Name</th>
                        <th className="text-left py-2 px-3 font-semibold text-gray-900">Purpose</th>
                        <th className="text-left py-2 px-3 font-semibold text-gray-900">Duration</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-gray-100">
                        <td className="py-2 px-3 text-gray-700 font-mono text-xs">cookieConsent</td>
                        <td className="py-2 px-3 text-gray-700">Stores your cookie preferences</td>
                        <td className="py-2 px-3 text-gray-700">12 months</td>
                      </tr>
                      <tr className="border-b border-gray-100">
                        <td className="py-2 px-3 text-gray-700 font-mono text-xs">session_id</td>
                        <td className="py-2 px-3 text-gray-700">Maintains your session state</td>
                        <td className="py-2 px-3 text-gray-700">Session</td>
                      </tr>
                      <tr>
                        <td className="py-2 px-3 text-gray-700 font-mono text-xs">XSRF-TOKEN</td>
                        <td className="py-2 px-3 text-gray-700">Security protection against cross-site attacks</td>
                        <td className="py-2 px-3 text-gray-700">Session</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Analytics Cookies */}
              <div className="bg-blue-50 border-l-4 border-blue-600 p-6 mb-6 rounded-r-lg">
                <h3 className="text-xl font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <i className="ri-bar-chart-line text-blue-600"></i>
                  2. Analytics Cookies (Performance)
                </h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously. 
                  This helps us improve our website's performance and user experience.
                </p>
                <div className="bg-white rounded-lg p-4 mt-4">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-2 px-3 font-semibold text-gray-900">Cookie Name</th>
                        <th className="text-left py-2 px-3 font-semibold text-gray-900">Purpose</th>
                        <th className="text-left py-2 px-3 font-semibold text-gray-900">Duration</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-gray-100">
                        <td className="py-2 px-3 text-gray-700 font-mono text-xs">_ga</td>
                        <td className="py-2 px-3 text-gray-700">Google Analytics - distinguishes users</td>
                        <td className="py-2 px-3 text-gray-700">2 years</td>
                      </tr>
                      <tr className="border-b border-gray-100">
                        <td className="py-2 px-3 text-gray-700 font-mono text-xs">_gid</td>
                        <td className="py-2 px-3 text-gray-700">Google Analytics - distinguishes users</td>
                        <td className="py-2 px-3 text-gray-700">24 hours</td>
                      </tr>
                      <tr>
                        <td className="py-2 px-3 text-gray-700 font-mono text-xs">_gat</td>
                        <td className="py-2 px-3 text-gray-700">Google Analytics - throttles request rate</td>
                        <td className="py-2 px-3 text-gray-700">1 minute</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Marketing Cookies */}
              <div className="bg-purple-50 border-l-4 border-purple-600 p-6 mb-6 rounded-r-lg">
                <h3 className="text-xl font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <i className="ri-megaphone-line text-purple-600"></i>
                  3. Marketing Cookies (Targeting/Advertising)
                </h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  These cookies are used to track visitors across websites to display relevant advertisements. 
                  They help us measure the effectiveness of our marketing campaigns and provide you with personalized content.
                </p>
                <div className="bg-white rounded-lg p-4 mt-4">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-2 px-3 font-semibold text-gray-900">Cookie Name</th>
                        <th className="text-left py-2 px-3 font-semibold text-gray-900">Purpose</th>
                        <th className="text-left py-2 px-3 font-semibold text-gray-900">Duration</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-gray-100">
                        <td className="py-2 px-3 text-gray-700 font-mono text-xs">_fbp</td>
                        <td className="py-2 px-3 text-gray-700">Facebook Pixel - tracks conversions</td>
                        <td className="py-2 px-3 text-gray-700">3 months</td>
                      </tr>
                      <tr className="border-b border-gray-100">
                        <td className="py-2 px-3 text-gray-700 font-mono text-xs">IDE</td>
                        <td className="py-2 px-3 text-gray-700">Google DoubleClick - ad targeting</td>
                        <td className="py-2 px-3 text-gray-700">13 months</td>
                      </tr>
                      <tr>
                        <td className="py-2 px-3 text-gray-700 font-mono text-xs">fr</td>
                        <td className="py-2 px-3 text-gray-700">Facebook - delivers targeted advertising</td>
                        <td className="py-2 px-3 text-gray-700">3 months</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </section>

            {/* Third-Party Cookies */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <i className="ri-links-line text-amber-600"></i>
                Third-Party Cookies
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Some cookies on our website are placed by third-party services. We use the following third-party services:
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-amber-600 text-xl flex-shrink-0 mt-0.5"></i>
                  <div>
                    <strong className="text-gray-900">Google Analytics</strong>
                    <span className="text-gray-700"> — To analyze website traffic and user behavior</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-amber-600 text-xl flex-shrink-0 mt-0.5"></i>
                  <div>
                    <strong className="text-gray-900">Facebook Pixel</strong>
                    <span className="text-gray-700"> — To track conversions and deliver targeted advertising</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-amber-600 text-xl flex-shrink-0 mt-0.5"></i>
                  <div>
                    <strong className="text-gray-900">Google Ads</strong>
                    <span className="text-gray-700"> — To display relevant advertisements across the web</span>
                  </div>
                </li>
              </ul>
            </section>

            {/* How to Manage Cookies */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <i className="ri-settings-3-line text-amber-600"></i>
                How to Manage Cookies
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                You have the right to decide whether to accept or reject cookies. You can manage your cookie preferences through:
              </p>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">1. Cookie Consent Banner</h3>
                <p className="text-gray-700 leading-relaxed">
                  When you first visit our website, you'll see a cookie consent banner where you can choose to accept all cookies, 
                  reject non-essential cookies, or manage your preferences in detail.
                </p>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">2. Browser Settings</h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Most web browsers allow you to control cookies through their settings. Here's how to manage cookies in popular browsers:
                </p>
                <ul className="space-y-2 ml-4">
                  <li className="text-gray-700">
                    <strong>Chrome:</strong> Settings &gt; Privacy and security &gt; Cookies and other site data
                  </li>
                  <li className="text-gray-700">
                    <strong>Firefox:</strong> Options &gt; Privacy &amp; Security &gt; Cookies and Site Data
                  </li>
                  <li className="text-gray-700">
                    <strong>Safari:</strong> Preferences &gt; Privacy &gt; Cookies and website data
                  </li>
                  <li className="text-gray-700">
                    <strong>Edge:</strong> Settings &gt; Cookies and site permissions &gt; Cookies and site data
                  </li>
                </ul>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                <div className="flex items-start gap-3">
                  <i className="ri-alert-line text-red-600 text-2xl flex-shrink-0"></i>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Important Note</h3>
                    <p className="text-gray-700 leading-relaxed">
                      Please note that blocking or deleting cookies may impact your experience on our website and limit the functionality available to you. 
                      Some features may not work properly without cookies enabled.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Your Rights Under GDPR */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <i className="ri-shield-user-line text-amber-600"></i>
                Your Rights Under GDPR
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Under the General Data Protection Regulation (GDPR), you have the following rights regarding cookies and tracking:
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-amber-600 text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700"><strong>Right to be informed</strong> — You have the right to know what cookies are being used</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-amber-600 text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700"><strong>Right to consent</strong> — You must give explicit consent for non-essential cookies</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-amber-600 text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700"><strong>Right to withdraw consent</strong> — You can change your cookie preferences at any time</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-amber-600 text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700"><strong>Right to access</strong> — You can request information about the data collected via cookies</span>
                </li>
              </ul>
            </section>

            {/* Changes to This Policy */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <i className="ri-refresh-line text-amber-600"></i>
                Changes to This Cookie Policy
              </h2>
              <p className="text-gray-700 leading-relaxed">
                We may update this Cookie Policy from time to time to reflect changes in our practices or for legal, operational, or regulatory reasons. 
                We will notify you of any significant changes by posting the new policy on this page with an updated "Last Updated" date. 
                We encourage you to review this policy periodically.
              </p>
            </section>

            {/* Contact Us */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <i className="ri-mail-line text-amber-600"></i>
                Contact Us
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                If you have any questions about our use of cookies or this Cookie Policy, please contact us:
              </p>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <i className="ri-building-line text-amber-600 text-xl flex-shrink-0"></i>
                    <div>
                      <p className="font-semibold text-gray-900">Nex Pro Travels Limited (trading as Alwaz Travel)</p>
                      <p className="text-gray-700">Company Number: 15976699</p>
                      <p className="text-gray-700">Registered in England and Wales</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <i className="ri-mail-line text-amber-600 text-xl"></i>
                    <a href="mailto:contact@alwaz.travel" className="text-amber-600 hover:text-amber-700 font-medium">
                      contact@alwaz.travel
                    </a>
                  </div>
                  <div className="flex items-center gap-3">
                    <i className="ri-phone-line text-amber-600 text-xl"></i>
                    <a href="tel:+442045380363" className="text-amber-600 hover:text-amber-700 font-medium">
                      +44 204 538 0363
                    </a>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-map-pin-line text-amber-600 text-xl flex-shrink-0"></i>
                    <p className="text-gray-700">
                      128 City Road, London, United Kingdom, EC1V 2NX
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Related Policies */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <i className="ri-file-list-line text-amber-600"></i>
                Related Policies
              </h2>
              <div className="flex flex-wrap gap-3">
                <a
                  href="/privacy-policy"
                  className="inline-flex items-center gap-2 px-5 py-3 bg-gray-900 hover:bg-gray-800 text-white rounded-lg transition-colors whitespace-nowrap"
                >
                  <i className="ri-shield-line"></i>
                  Privacy Policy
                </a>
                <a
                  href="/terms-and-conditions"
                  className="inline-flex items-center gap-2 px-5 py-3 bg-gray-900 hover:bg-gray-800 text-white rounded-lg transition-colors whitespace-nowrap"
                >
                  <i className="ri-file-text-line"></i>
                  Terms &amp; Conditions
                </a>
              </div>
            </section>

          </div>
        </div>

        <Footer />
      </div>
    </Suspense>
  );
}